import { useMemo } from "react";
import { ArrowLeft, ArrowRight, Moon, ShieldCheck } from "lucide-react";
import { Overline, Ornament } from "@/components/Brand";
import { captureMeta } from "@/lib/lunar";

const SIGNS = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces", "I don't know"];
const HD = ["Manifestor", "Generator", "Manifesting Generator", "Projector", "Reflector", "I don't know"];

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="block text-xs tracking-wide text-[#7D5A4B] mb-1.5">
      {label} {required ? <span className="text-[#B76E60]">*</span> : <span className="text-[#B8892F]/80">(optional)</span>}
    </span>
    {children}
  </label>
);

export default function PersonalInfo({ profile, setProfile, onContinue, onBack }) {
  const meta = useMemo(() => captureMeta(new Date()), []);
  const set = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target.value }));
  const input = (k, props = {}) => <input data-testid={`${k}-input`} className="field" value={profile[k]} onChange={set(k)} {...props} />;
  const select = (k, opts, testid) => (
    <select data-testid={testid} className="field" value={profile[k]} onChange={set(k)}>
      <option value="">Select…</option>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  const valid = profile.firstName.trim().length > 0;

  return (
    <main data-testid="personal-info-screen" className="px-5 sm:px-10 pb-20">
      <div className="max-w-5xl mx-auto pt-4 sm:pt-10">
        <Overline>Before We Begin</Overline>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#4A2A20] mt-4">Tell us a little about you</h1>
        <p className="text-[#7D5A4B] mt-3 max-w-2xl">Only your first name is needed. Everything else is optional context for your roadmap and never changes your journey results.</p>
        <Ornament className="mt-6" />

        <form
          onSubmit={(e) => { e.preventDefault(); if (valid) onContinue(); }}
          className="grid lg:grid-cols-[1fr_320px] gap-8 mt-10"
        >
          <div className="glass-card rounded-3xl p-6 sm:p-8 space-y-8">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="First name" required>{input("firstName", { placeholder: "Your first name", autoComplete: "given-name", maxLength: 60, required: true })}</Field>
              <Field label="Last name">{input("lastName", { placeholder: "Your last name", autoComplete: "family-name", maxLength: 60 })}</Field>
              <div className="sm:col-span-2">
                <Field label="Email address">{input("email", { type: "email", placeholder: "you@example.com", autoComplete: "email" })}</Field>
              </div>
            </div>
            <div>
              <div className="font-serif-display text-xl text-[#4A2A20] mb-4">Birth details</div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Date of birth">{input("dob", { type: "date" })}</Field>
                <Field label="Time of birth">{input("birthTime", { type: "time" })}</Field>
                <Field label="Birth city">{input("birthCity", { placeholder: "City", maxLength: 80 })}</Field>
                <Field label="Birth state / region">{input("birthState", { placeholder: "State or region", maxLength: 80 })}</Field>
                <div className="sm:col-span-2"><Field label="Birth country">{input("birthCountry", { placeholder: "Country", maxLength: 80 })}</Field></div>
              </div>
            </div>
            <div>
              <div className="font-serif-display text-xl text-[#4A2A20] mb-4">Cosmic identifiers</div>
              <div className="grid sm:grid-cols-3 gap-5">
                <Field label="Sun sign">{select("sunSign", SIGNS, "sun-sign-select")}</Field>
                <Field label="Moon sign">{select("moonSign", SIGNS, "moon-sign-select")}</Field>
                <Field label="Human Design type">{select("humanDesign", HD, "human-design-select")}</Field>
              </div>
            </div>
          </div>

          <aside className="space-y-5">
            <div className="rounded-3xl p-6 bg-gradient-to-br from-[#FBEFEA] to-[#FAF3E8] border border-[#C9A24A]/30" data-testid="auto-capture-panel">
              <Overline>Captured automatically</Overline>
              <div className="flex items-center gap-4 mt-5">
                <MoonGlyph illumination={meta.lunar.illumination} waxing={meta.lunar.age < 14.77} />
                <div>
                  <div className="font-serif-display text-2xl text-[#4A2A20]" data-testid="lunar-phase-display">{meta.lunar.name}</div>
                  <div className="text-xs text-[#7D5A4B]">{meta.lunar.illumination}% illuminated</div>
                </div>
              </div>
              <dl className="mt-5 space-y-2 text-sm">
                <Row k="Date" v={meta.date} />
                <Row k="Time" v={meta.time} />
                <Row k="Time zone" v={meta.timeZone} />
              </dl>
            </div>
            <div className="rounded-2xl p-5 border border-[#C9A24A]/25 bg-white/60 flex gap-3 text-sm text-[#7D5A4B]">
              <ShieldCheck className="w-5 h-5 text-[#B76E60] shrink-0" />
              Your answers stay only in this browser tab. Nothing is saved, and everything clears when you start over or leave.
            </div>
            <div className="flex flex-col gap-3">
              <button type="submit" data-testid="continue-assessment-btn" disabled={!valid} className="btn-rose rounded-full px-8 py-4 font-medium inline-flex items-center justify-center gap-2">
                Continue to Assessment <ArrowRight className="w-4 h-4" />
              </button>
              <button type="button" data-testid="info-back-btn" onClick={onBack} className="btn-ghost-gold rounded-full px-8 py-3 text-sm inline-flex items-center justify-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            </div>
          </aside>
        </form>
      </div>
    </main>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 border-b border-[#C9A24A]/15 pb-2">
    <dt className="text-[#7D5A4B]">{k}</dt>
    <dd className="text-[#4A2A20] text-right">{v}</dd>
  </div>
);

export const MoonGlyph = ({ illumination, waxing, size = 56 }) => {
  const k = illumination / 100;
  const rx = Math.abs(1 - 2 * k) * 24;
  const lit = k > 0.5;
  return (
    <svg width={size} height={size} viewBox="0 0 56 56" aria-hidden>
      <circle cx="28" cy="28" r="24" fill="#EAD9C4" stroke="#C9A24A" strokeWidth="1" />
      <path
        d={`M28 4 A24 24 0 0 ${waxing ? 1 : 0} 28 52 A${rx} 24 0 0 ${(waxing ? !lit : lit) ? 1 : 0} 28 4`}
        fill="#FFF6E3"
      />
    </svg>
  );
};
